import React, { useState } from "react";
import { createEducation, updateEducation, deleteEducation } from "../services/educationService";
import { VscEdit, VscTrash } from "react-icons/vsc";

function EducationManager({ userId, educations, onEducationsChange }) {
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [formData, setFormData] = useState({
        school_name: "",
        degree: "",
        field_of_study: "",
        start_date: "",
        end_date: "",
        description: "",
    });

    const resetForm = () => {
        setFormData({
            school_name: "",
            degree: "",
            field_of_study: "",
            start_date: "",
            end_date: "",
            description: "",
        });
        setEditingId(null);
        setShowAddForm(false);
        setError("");
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!formData.school_name || !formData.start_date) return;

        setSaving(true);
        setError("");

        // Convert empty date fields to null
        const dataToSubmit = {
            ...formData,
            end_date: formData.end_date || null,
        };

        const { data, error: addError } = await createEducation(userId, dataToSubmit);

        setSaving(false);

        if (addError) {
            setError(`Failed to add education: ${addError.message}`);
        } else if (data) {
            const updatedEducations = [data, ...educations].sort((a, b) =>
                new Date(b.start_date) - new Date(a.start_date)
            );
            onEducationsChange(updatedEducations);
            resetForm();
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!formData.school_name || !formData.start_date) return;

        setSaving(true);
        setError("");

        // Convert empty date fields to null
        const dataToSubmit = {
            ...formData,
            end_date: formData.end_date || null,
        };

        const { data, error: updateError } = await updateEducation(editingId, dataToSubmit);

        setSaving(false);

        if (updateError) {
            setError(`Failed to update education: ${updateError.message}`);
        } else if (data) {
            const updatedEducations = educations.map(edu =>
                edu.id === editingId ? data : edu
            ).sort((a, b) =>
                new Date(b.start_date) - new Date(a.start_date)
            );
            onEducationsChange(updatedEducations);
            resetForm();
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this education entry?")) return;

        const { error } = await deleteEducation(id);
        if (!error) {
            onEducationsChange(educations.filter(edu => edu.id !== id));
        }
    };

    const startEdit = (edu) => {
        setFormData({
            school_name: edu.school_name,
            degree: edu.degree || "",
            field_of_study: edu.field_of_study || "",
            start_date: edu.start_date,
            end_date: edu.end_date || "",
            description: edu.description || "",
        });
        setEditingId(edu.id);
        setShowAddForm(false);
    };

    return (
        <div className="form-section">
            <div className="section-header">
                <h3>Education</h3>
                {!showAddForm && !editingId && (
                    <button
                        type="button"
                        className="btn-add"
                        onClick={() => setShowAddForm(true)}
                    >
                        + Add Education
                    </button>
                )}
            </div>

            {(showAddForm || editingId) && (
                <form className="education-form" onSubmit={editingId ? handleUpdate : handleAdd}>
                    {error && <div className="error-message">{error}</div>}
                    <div className="field-pair">
                        <label>
                            School/University *
                            <input
                                type="text"
                                value={formData.school_name}
                                onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                                placeholder="School or university name"
                                required
                            />
                        </label>
                        <label>
                            Degree
                            <input
                                type="text"
                                value={formData.degree}
                                onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                                placeholder="e.g., Bachelor of Science"
                            />
                        </label>
                    </div>
                    <div className="field-pair">
                        <label>
                            Field of Study
                            <input
                                type="text"
                                value={formData.field_of_study}
                                onChange={(e) => setFormData({ ...formData, field_of_study: e.target.value })}
                                placeholder="e.g., Sports Science"
                            />
                        </label>
                    </div>
                    <div className="field-pair">
                        <label>
                            Start Date *
                            <input
                                type="date"
                                value={formData.start_date}
                                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                required
                                max="9999-12-31"
                                min="1900-01-01"
                            />
                        </label>
                        <label>
                            End Date
                            <input
                                type="date"
                                value={formData.end_date}
                                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                max="9999-12-31"
                                min="1900-01-01"
                            />
                            <span className="field-hint">Leave blank if current</span>
                        </label>
                    </div>
                    <label>
                        Description
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Achievements, courses, honors, etc..."
                            rows={2}
                        />
                    </label>
                    <div className="form-actions-inline">
                        <button type="submit" className="btn-primary-edu" disabled={saving}>
                            {saving ? "Saving..." : (editingId ? "Update" : "Add")}
                        </button>
                        <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {educations.length > 0 && (
                <div className="education-list">
                    {educations.map(edu => (
                        <div key={edu.id} className="education-item">
                            <div className="education-item-content">
                                <h4>{edu.school_name}</h4>
                                <p className="edu-degree">
                                    {edu.degree && edu.field_of_study && `${edu.degree} in ${edu.field_of_study}`}
                                    {edu.degree && !edu.field_of_study && edu.degree}
                                    {!edu.degree && edu.field_of_study && edu.field_of_study}
                                </p>
                                <p className="edu-date">
                                    {new Date(edu.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                    {' - '}
                                    {edu.end_date ? new Date(edu.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
                                </p>
                                {edu.description && <p className="edu-description">{edu.description}</p>}
                            </div>
                            <div className="education-item-actions">
                                <button
                                    type="button"
                                    className="btn-icon-edit"
                                    onClick={() => startEdit(edu)}
                                    title="Edit"
                                >
                                    <VscEdit color="#dc2626" size={18} />
                                </button>
                                <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => handleDelete(edu.id)}
                                    title="Delete"
                                >
                                    <VscTrash color="rgba(255,255,255,0.7)" size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default EducationManager;
